import { ChangeEvent, FC, useState } from 'react'

import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'

import {
  InsuranceAgeError,
  MAX_ADULT_AGE,
  MAX_INFANT_AGE,
  MIN_INSURANCE_AGE,
  getInsuranceCountLimit,
  resizeInsuranceAges,
} from '../../../../model/travelers'
import { TravelerCounter } from '../traveler-counter'

import s from './styles.module.scss'

interface TravelersCardProps {
  travelerAges: string[]
  infantAges: string[]
  travelerAgeErrors: Array<InsuranceAgeError | null>
  infantAgeErrors: Array<InsuranceAgeError | null>
  onTravelerAgesChange: (ages: string[]) => void
  onInfantAgesChange: (ages: string[]) => void
}

const touchAge = (touchedAges: boolean[], index: number) => {
  if (touchedAges[index]) return touchedAges

  const nextTouchedAges = [...touchedAges]
  nextTouchedAges[index] = true
  return nextTouchedAges
}

export const TravelersCard: FC<TravelersCardProps> = ({
  travelerAges,
  infantAges,
  travelerAgeErrors,
  infantAgeErrors,
  onTravelerAgesChange,
  onInfantAgesChange,
}) => {
  const { t } = useTranslation()
  const [touchedTravelerAges, setTouchedTravelerAges] = useState<boolean[]>([])
  const [touchedInfantAges, setTouchedInfantAges] = useState<boolean[]>([])
  const canAddInfant = getInsuranceCountLimit(travelerAges.length) > 0

  const updateAge = (
    ages: string[],
    index: number,
    event: ChangeEvent<HTMLInputElement>,
    onChange: (nextAges: string[]) => void,
  ) => {
    const nextAges = [...ages]
    nextAges[index] = event.currentTarget.value
    onChange(nextAges)
  }

  const getAgeFieldError = (
    error: InsuranceAgeError | null,
    isTouched?: boolean,
  ): FieldError | undefined =>
    isTouched && error
      ? {
          type: 'validate',
          message: t(`insurance.vzr.travelers.errors.${error}`),
        }
      : undefined

  return (
    <Paper
      className={s.card}
      padding={[20, 16]}
      radius="20px"
    >
      <h2 className={clsx(s.title, 'bold-17')}>
        {t('insurance.vzr.travelers.title')}
      </h2>
      <TravelerCounter
        title={t('insurance.vzr.travelers.adults')}
        hint={t('insurance.vzr.travelers.adults_hint')}
        min={1}
        max={getInsuranceCountLimit(infantAges.length)}
        value={travelerAges.length}
        onChange={(count) => {
          setTouchedTravelerAges((current) => current.slice(0, count))
          onTravelerAgesChange(resizeInsuranceAges(travelerAges, count))
        }}
      />
      <div className={s.ageFields}>
        {travelerAges.map((age, index) => (
          <InputWithLabel
            key={index}
            containerClassName={s.fieldContainer}
            type="number"
            min={MIN_INSURANCE_AGE}
            max={MAX_ADULT_AGE}
            step={1}
            inputMode="numeric"
            label={t('insurance.vzr.travelers.age', {
              number: index + 1,
            })}
            error={getAgeFieldError(
              travelerAgeErrors[index],
              touchedTravelerAges[index],
            )}
            value={age}
            onChange={(event) => {
              setTouchedTravelerAges((current) => touchAge(current, index))
              updateAge(travelerAges, index, event, onTravelerAgesChange)
            }}
          />
        ))}
      </div>

      <div className={s.infantSection}>
        {infantAges.length ? (
          <>
            <TravelerCounter
              title={t('insurance.vzr.travelers.infants')}
              hint={t('insurance.vzr.travelers.infants_hint')}
              min={0}
              max={getInsuranceCountLimit(travelerAges.length)}
              value={infantAges.length}
              onChange={(count) => {
                setTouchedInfantAges((current) => current.slice(0, count))
                onInfantAgesChange(resizeInsuranceAges(infantAges, count))
              }}
            />
            <div className={s.ageFields}>
              {infantAges.map((age, index) => (
                <InputWithLabel
                  key={index}
                  containerClassName={s.fieldContainer}
                  type="number"
                  min={MIN_INSURANCE_AGE}
                  max={MAX_INFANT_AGE}
                  step={1}
                  inputMode="numeric"
                  label={t('insurance.vzr.travelers.months', {
                    number: index + 1,
                  })}
                  error={getAgeFieldError(
                    infantAgeErrors[index],
                    touchedInfantAges[index],
                  )}
                  value={age}
                  onChange={(event) => {
                    setTouchedInfantAges((current) => touchAge(current, index))
                    updateAge(infantAges, index, event, onInfantAgesChange)
                  }}
                />
              ))}
            </div>
          </>
        ) : (
          <button
            type="button"
            className={s.addInfant}
            disabled={!canAddInfant}
            onClick={() => {
              setTouchedInfantAges([])
              onInfantAgesChange([''])
            }}
          >
            <AppIcon
              id={AllIcons.PLUS}
              width={18}
              height={18}
            />
            <span className={clsx(s.addInfantLabel, 'medium-14')}>
              {t('insurance.vzr.travelers.add_infant')}{' '}
              <span>{t('insurance.vzr.travelers.add_infant_hint')}</span>
            </span>
          </button>
        )}
      </div>
    </Paper>
  )
}
